import type { Entitlement } from '../../services/entitlementsApi';
import type { EngineSubscription } from '../../services/billingApi';
import { isRenewed } from './planExpiry';

/**
 * Activation-poll rules for UpgradeModal.
 *
 * Lives in its own module — NOT in UpgradeModal.tsx — for the same reason
 * planToSelect.ts does: that component imports the billing API, which pulls in
 * Firebase, which jsdom cannot load (house rule: RTL leaf components only).
 * Keeping these rules importable without that chain is what makes them
 * testable at all. `import type` is erased at compile time, so it adds no
 * runtime dependency.
 */

/**
 * Poll backoff (ms), ~2.5 minutes in total.
 *
 * Was 2,4,8,15,30 = 59s, sized for a pipeline that did not exist: until
 * 2026-09-21 a grant crossed a gateway forward that ran every 10 minutes and
 * an engine push that was drained once a day, so this poll timed out on
 * essentially every real purchase. Now the gateway forwards every minute and
 * the engine delivers inline, so a grant typically lands inside ~70s. The
 * budget covers that with headroom; the timed-out state stays honest for the
 * rare case beyond it.
 */
export const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 10000, 15000, 20000, 30000, 30000, 30000];

/**
 * True once the purchase is really live for this vertical and tier.
 *
 * `status === 'active'` is required, not optional. Without it, a lawyer who
 * already held an engine-pushed row at the same tier — a trialing Pro row,
 * then buying Pro — matched on the FIRST 2-second poll and saw "Your plan is
 * active" before any payment had been processed. FixPaymentDialog found and
 * fixed exactly this for its own flow; its comment noted UpgradeModal's
 * predicate was only safe when an upgrade changes the tier, which is not true
 * of a trial conversion or a same-tier repurchase.
 */
export function isActivated(
  entitlements: Entitlement[],
  target: { vertical: string; tier: string },
): boolean {
  return entitlements.some(
    (e) =>
      e.vertical === target.vertical &&
      e.source === 'engine_push' &&
      e.tier === target.tier &&
      e.status === 'active',
  );
}

/**
 * Human date for a deferred first charge, e.g. "11 Oct 2026". Returns null for
 * a missing or unparseable value so the caller falls back to the ordinary
 * activation poll rather than printing "Invalid Date" to a paying lawyer.
 */
export function formatFirstChargeDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Copy for UpgradeModal's 'scheduled' stage — reached whenever
 *  `first_charge_at` is set, which happens for BOTH a trial-conversion's
 *  deferred first charge and an upgrade's credit-as-free-time deferred
 *  first charge. The two are NOT the same situation and must not share
 *  wording: a trial conversion changes nothing yet (access continues on the
 *  trial as before), while an upgrade's tier access changes IMMEDIATELY on
 *  the mandate webhook — only the bill is deferred, crediting the unused
 *  days on the plan being upgraded FROM. Telling an upgrading lawyer
 *  "nothing has changed, you'll be charged in N days" would be false: their
 *  Max access is already live by the time this stage renders. Extracted
 *  here (not inline in the component) for the same Firebase-free
 *  testability reason every other pure rule in this file is. */
export function scheduledStageCopy(args: {
  upgrade: boolean;
  firstChargeLabel: string | null;
  /** Pre-formatted price clause, e.g. " of ₹2,999 (incl. GST)", or '' when
   *  no plan is selected yet — built by the caller the same way the
   *  'scheduled' stage already does today, kept outside this function so it
   *  stays a pure string-template decision with no currency-formatting
   *  concerns of its own. */
  priceClause: string;
}): { title: string; headline: string; body: string } {
  const { upgrade, firstChargeLabel, priceClause } = args;
  if (upgrade) {
    return {
      title: 'Your plan is upgraded',
      headline: 'Your plan has been upgraded — nothing has been charged today.',
      body:
        `Your new access is live right now. Your first charge${priceClause} will be on ` +
        `${firstChargeLabel}. We credited your unused days on the plan you upgraded from, ` +
        `so you pay nothing extra for them.`,
    };
  }
  return {
    title: 'Your plan is set up',
    headline: 'Your free trial continues — nothing has been charged today.',
    body:
      `Your first charge${priceClause} will be on ${firstChargeLabel}. ` +
      `You keep full access until then, and your plan continues automatically after.`,
  };
}

/**
 * True once a RENEWAL is live in both places the card reads: the engine has
 * moved the period end (`isRenewed`) AND the engine's push has reached this
 * app's entitlement (`isActivated`). The push lands after the engine records
 * the payment, so stopping at the first left the lawyer looking at
 * "Expired · 0 matter slots" beside the new paid-until date.
 */
export function isRenewalLive(
  subs: EngineSubscription[],
  entitlements: Entitlement[],
  vertical: string,
  renewal: { planCode: string; currentPeriodEnd: string | null },
  tier: string,
): boolean {
  return isRenewed(subs, vertical, renewal) && isActivated(entitlements, { vertical, tier });
}
